import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { getModalFilters, transportAssignmentsSlice } from '../../../reducers/transportAssignmentsSlice';
import { DateInput, validateDateTimeFields } from '../../../sharedComponents/dateHelper';
import { FilterModalButton, FilterModalFooter } from '../../../sharedComponents/filter/FilterModal';
import { LocationFilter, LocationValue } from '../../../sharedComponents/filter/LocationFilter';
import { DispatchTypeFilter } from '../../../sharedComponents/filter/DispatchTypeFilter';
import { MeansOfTransportType, VehicleType } from '../../../sharedComponents/common.types';
import { getValidDate, getValidText } from '../../../sharedComponents/inputValidations';
import { DateRangeFilter } from '../../../sharedComponents/filter/DateRangeFilter';
import { MeansOfTransportTypeFilter } from '../../../sharedComponents/filter/MeansOfTransportTypeFilter';
import { ManufacturerFilter } from '../../../sharedComponents/filter/ManufacturerFilter';

export interface LocalModalFilters {
    startDateFilter: DateInput;
    endDateFilter: DateInput;
    pickUpLocation: LocationValue;
    deliveryLocation: LocationValue;
    meansOfTransportTypeFilter: MeansOfTransportType | undefined;
    vehicleTypeFilter: VehicleType | undefined;
    manufacturerFilter: string | undefined;
}

const UNSET_FILTERS: LocalModalFilters = {
    startDateFilter: undefined,
    endDateFilter: undefined,
    pickUpLocation: undefined,
    deliveryLocation: undefined,
    meansOfTransportTypeFilter: undefined,
    vehicleTypeFilter: undefined,
    manufacturerFilter: undefined,
};

interface TransportAssignmentFilterModalContentProps {
    localFilters: LocalModalFilters;
    setLocalFilters: Dispatch<SetStateAction<LocalModalFilters>>;
}

const TransportAssignmentFilterModalContent = ({
    localFilters,
    setLocalFilters,
}: TransportAssignmentFilterModalContentProps) => {
    return (
        <div className="margin-bottom-20">
            <DateRangeFilter
                timeframeFilterTitle="outboundPortal.transportAssignments.table.toolbar.filter.pickUpTimeframe"
                startDateFilterName="startDateFilter"
                endDateFilterName="endDateFilter"
                startDate={localFilters.startDateFilter}
                endDate={localFilters.endDateFilter}
                setLocalFilters={setLocalFilters}
            />
            <hr className="margin-top-20 margin-bottom-20" />
            <LocationFilter
                headerMessageId="outboundOrderBook.transportAssignments.filter.pickUpLocation"
                value={localFilters.pickUpLocation}
                onLocationChange={(pickUpLocation) => {
                    setLocalFilters((prevState) => ({ ...prevState, pickUpLocation }));
                }}
            />
            <div className="margin-top-20">
                <LocationFilter
                    headerMessageId="outboundOrderBook.transportAssignments.filter.deliveryLocation"
                    value={localFilters.deliveryLocation}
                    onLocationChange={(deliveryLocation) => {
                        setLocalFilters((prevState) => ({ ...prevState, deliveryLocation }));
                    }}
                />
            </div>
            <div className="margin-top-20">
                <MeansOfTransportTypeFilter
                    filterName="meansOfTransportTypeFilter"
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                />
            </div>
            <div className="margin-top-20">
                <DispatchTypeFilter localFilters={localFilters} setLocalFilters={setLocalFilters} />
            </div>
            <div className="margin-top-20">
                <ManufacturerFilter localFilters={localFilters} setLocalFilters={setLocalFilters} />
            </div>
        </div>
    );
};

export const TransportAssignmentFilterModal = () => {
    const dispatch = useAppDispatch();
    const modalFilters = useAppSelector(getModalFilters);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [localFilters, setLocalFilters] = useState<LocalModalFilters>(UNSET_FILTERS);

    const isFilterButtonActive = Object.values(modalFilters).some((value) => value !== undefined && value !== '');

    const handleOnClickApply = () => {
        setLocalFilters({
            ...localFilters,
            pickUpLocation: getValidText(localFilters.pickUpLocation),
            deliveryLocation: getValidText(localFilters.deliveryLocation),
            manufacturerFilter: getValidText(localFilters.manufacturerFilter),
        });
        dispatch(
            transportAssignmentsSlice.actions.setModalFilters({
                startDateFilter: getValidDate(localFilters.startDateFilter),
                endDateFilter: getValidDate(localFilters.endDateFilter),
                pickUpLocation: getValidText(localFilters.pickUpLocation),
                deliveryLocation: getValidText(localFilters.deliveryLocation),
                manufacturerFilter: getValidText(localFilters.manufacturerFilter),
                meansOfTransportTypeFilter: localFilters.meansOfTransportTypeFilter,
                vehicleTypeFilter: localFilters.vehicleTypeFilter,
            }),
        );
        setShowFilterModal(false);
    };

    const handleOnClickCancel = () => {
        setLocalFilters(modalFilters);
        setShowFilterModal(false);
    };

    const resetFilters = () => {
        setLocalFilters(UNSET_FILTERS);
    };

    const { hasStartDateTimeError, hasEndDateTimeError, hasTimeRangeOrderError } = useMemo(
        () => validateDateTimeFields(localFilters.startDateFilter, localFilters.endDateFilter),
        [localFilters],
    );

    return (
        <>
            <FilterModalButton isFilterActive={isFilterButtonActive} setShowFilterModal={setShowFilterModal} />
            <Dialog
                show={showFilterModal}
                bsSize={Dialog.SIZE_SM}
                title={<FormattedMessage id="outboundOrderBook.common.table.toolbar.filter.title" />}
                body={
                    <TransportAssignmentFilterModalContent
                        localFilters={localFilters}
                        setLocalFilters={setLocalFilters}
                    />
                }
                footer={
                    <FilterModalFooter
                        handleOnClickApply={handleOnClickApply}
                        handleOnClickCancel={handleOnClickCancel}
                        resetFilters={resetFilters}
                        isApplyButtonDisabled={hasTimeRangeOrderError || hasStartDateTimeError || hasEndDateTimeError}
                    />
                }
                onClose={handleOnClickCancel}
            />
        </>
    );
};
